import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import type { BlobDrainStatusResponse } from './types';

// The dashboard is computed by a background job (Queued::BlobDrainStatusJob) to
// avoid running whole-table aggregates inside the 30s web request. The endpoint
// returns { status: 'pending' } until the result is cached, then
// { status: 'ready', data }. The atom polls until ready and resolves with the
// data payload, so consumers see the same shape as before.
type StatusEnvelope =
  { status: 'pending' } | { status: 'ready'; data: BlobDrainStatusResponse };

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90; // ~3 min ceiling before surfacing an error

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const refreshCounterAtom = atom(0);

const blobDrainStatusBaseAtom = atom(
  async (get): Promise<BlobDrainStatusResponse> => {
    const refreshCount = get(refreshCounterAtom);

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      // A manual refresh (counter incremented) forces a fresh recompute.
      const forceRefresh = refreshCount > 0 && attempt === 0;
      const path = forceRefresh
        ? '/admin/blob_drain_status?refresh=true'
        : '/admin/blob_drain_status';

      const response = await request.get<StatusEnvelope>(path);
      if ('error' in response) {
        throw new Error(response.error);
      }
      if (response.status === 'ready') {
        return response.data;
      }

      await sleep(POLL_INTERVAL_MS);
    }

    throw new Error('Timed out waiting for drain status to compute');
  }
);

const blobDrainStatusLoadableAtom = loadable(blobDrainStatusBaseAtom);

export const blobDrainStatusAtom = atom(
  (get) => get(blobDrainStatusLoadableAtom),
  (_get, set) => {
    set(refreshCounterAtom, (c) => c + 1);
  }
);
